// MaturaDigitOnceClosedTask.js
// Zadanie zamknięte – ile jest liczb trzycyfrowych spełniających
// warunek wystąpienia cyfry dokładnie raz + warunek dodatkowy

import React from "react";
import { InlineMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { shuffle } from "../../../../utils/shuffle";

// ===== UTIL =====

function rnd(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randint(a, b) {
  return Math.floor(Math.random() * (b - a + 1)) + a;
}

// policzenie liczb trzycyfrowych spełniających warunek dokładnie raz cyfry d
function countDigitOnce(d, ruleFn) {
  let cnt = 0;
  for (let n = 100; n <= 999; n += 1) {
    const s = String(n);
    const occurrences = [...s].filter((c) => c === String(d)).length;
    if (occurrences === 1 && ruleFn(n)) cnt += 1;
  }
  return cnt;
}

// policzenie liczb spełniających co najmniej raz
function countDigitAtLeastOnce(d, ruleFn) {
  let cnt = 0;
  for (let n = 100; n <= 999; n += 1) {
    const s = String(n);
    const occurrences = [...s].filter((c) => c === String(d)).length;
    if (occurrences >= 1 && ruleFn(n)) cnt += 1;
  }
  return cnt;
}

// policzenie liczb trzycyfrowych, ale błędnie dopuszczając 0 w setkach (błąd ucznia)
function countWithLeadingZero(d, ruleFn) {
  let cnt = 0;
  for (let n = 0; n <= 999; n += 1) {
    const s = String(n).padStart(3, "0");
    const occurrences = [...s].filter((c) => c === String(d)).length;
    if (occurrences === 1 && ruleFn(Number(s))) cnt += 1;
  }
  return cnt;
}

// policzenie bez dodatkowych warunków (uczeń ignoruje parzystość/podzielność)
function countIgnoringRule(d) {
  let cnt = 0;
  for (let n = 100; n <= 999; n += 1) {
    const s = String(n);
    const occurrences = [...s].filter((c) => c === String(d)).length;
    if (occurrences === 1) cnt += 1;
  }
  return cnt;
}

// policzenie tylko jednego przypadku (np. cyfry d tylko w środku)
function countOnlyMiddle(d, ruleFn) {
  let cnt = 0;
  for (let h = 1; h <= 9; h += 1) {
    for (let t = 0; t <= 9; t += 1) {
      for (let u = 0; u <= 9; u += 1) {
        if (t === d && h !== d && u !== d) {
          const n = 100 * h + 10 * t + u;
          if (ruleFn(n)) cnt += 1;
        }
      }
    }
  }
  return cnt;
}

// ===== GŁÓWNY GENERATOR =====
// conditionKind: "even" | "odd" | "div5" | "none"

function generateJune2025Original() {
  const correct = countDigitOnce(0, (n) => n % 2 === 0);
  const optionValues = [108, 117, 126, 162];
  const options = optionValues.map((value, index) => ({
    label: String.fromCharCode("A".charCodeAt(0) + index),
    latex: String(value),
    value,
    isCorrect: value === correct,
  }));

  return {
    type: "MaturaDigitOnceClosedTask",
    data: {
      digit: 0,
      conditionKind: "even",
      correct,
      options,
      correctIndex: options.findIndex((option) => option.isCorrect),
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 28,
    },
  };
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 27:
 *   trzycyfrowe NIEPARZYSTE, w których cyfra 0 występuje dokładnie raz.
 *   Tylko gdy 0 jest na miejscu dziesiątek: 9·1·5 = 45  (A).
 */
function generateMay2025Original() {
  const correct = countDigitOnce(0, (n) => n % 2 === 1);
  const optionValues = [45, 50, 54, 81];
  const options = optionValues.map((value, index) => ({
    label: String.fromCharCode("A".charCodeAt(0) + index),
    latex: String(value),
    value,
    isCorrect: value === correct,
  }));

  return {
    type: "MaturaDigitOnceClosedTask",
    data: {
      digit: 0,
      conditionKind: "odd",
      correct,
      options,
      correctIndex: options.findIndex((option) => option.isCorrect),
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 27,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  const type = rnd([1, 2, 3, 4, 5, 6, 7]);

  let digit; // cyfra specjalna
  let ruleFn; // dodatkowy warunek
  let conditionKind; // typ warunku na liczbę

  if (type === 1) {
    // dokładnie jedno 0 + parzysta
    digit = 0;
    ruleFn = (n) => n % 2 === 0;
    conditionKind = "even";
  } else if (type === 2) {
    // dokładnie jedno 0 + podzielność przez 5
    digit = 0;
    ruleFn = (n) => n % 5 === 0;
    conditionKind = "div5";
  } else if (type === 3) {
    // dokładnie jedno 0 + nieparzysta
    digit = 0;
    ruleFn = (n) => n % 2 === 1;
    conditionKind = "odd";
  } else if (type === 4) {
    // dokładnie jedno d + parzysta
    digit = randint(1, 9);
    ruleFn = (n) => n % 2 === 0;
    conditionKind = "even";
  } else if (type === 5) {
    // dokładnie jedno d + nieparzysta
    digit = randint(1, 9);
    ruleFn = (n) => n % 2 === 1;
    conditionKind = "odd";
  } else if (type === 6) {
    // dokładnie jedno d + podzielność przez 5
    digit = randint(1, 9);
    ruleFn = (n) => n % 5 === 0;
    conditionKind = "div5";
  } else {
    // typ 5 — dokładnie jedno d, bez dodatkowych warunków
    digit = randint(1, 9);
    ruleFn = () => true;
    conditionKind = "none";
  }

  // wynik poprawny
  const correct = countDigitOnce(digit, ruleFn);

  // ==== DYSTRKTORY =====
  const distractorPool = [];

  // 1. co najmniej raz zamiast dokładnie raz
  distractorPool.push(countDigitAtLeastOnce(digit, ruleFn));

  // 2. pozwolenie na leading zero (000–999)
  distractorPool.push(countWithLeadingZero(digit, ruleFn));

  // 3. ignorowanie reguły parzystości/podzielności
  distractorPool.push(countIgnoringRule(digit));

  // 4. policzenie tylko jednego przypadku
  distractorPool.push(countOnlyMiddle(digit, ruleFn));

  // 5. „coś z sufitu” – w okolicy wyniku (symuluje złe dodanie)
  distractorPool.push(correct + randint(5, 40));

  // wybieramy 3 różne błędne
  let wrongValues = Array.from(
    new Set(distractorPool.filter((v) => v !== correct))
  );

  // na wszelki wypadek dokładamy sztuczne wartości,
  // jeśli z "prawdziwych" błędów nie wyszło >= 3 różnych
  let candidate = correct + 1;
  while (wrongValues.length < 3) {
    if (!wrongValues.includes(candidate)) {
      wrongValues.push(candidate);
    }
    candidate += 1;
  }

  const bad = shuffle(wrongValues).slice(0, 3);
  const rawNumbers = [correct, ...bad];

  const rawOptions = rawNumbers.map((v) => ({
    latex: `${v}`,
    value: v,
    isCorrect: v === correct,
  }));

  const shuffled = shuffle(rawOptions);
  const options = shuffled.map((opt, idx) => ({
    ...opt,
    label: String.fromCharCode("A".charCodeAt(0) + idx),
  }));
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaDigitOnceClosedTask",
    data: {
      digit,
      conditionKind,
      correct,
      options,
      correctIndex,
    },
  };
}

// ===== TREŚĆ ZADANIA =====

export function renderTask({ data }) {
  const { digit, conditionKind, options } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie maturalne – zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        Wszystkich trzycyfrowych liczb naturalnych
        {conditionKind === "even" && " parzystych"}
        {conditionKind === "odd" && " nieparzystych"}
        {conditionKind === "div5" && (
          <>
            {" "}
            podzielnych przez <InlineMath math={'5'} />
          </>
        )}
        , w których cyfra <InlineMath math={`${digit}`} /> występuje
        dokładnie raz, jest:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== INPUT ABCD (dla QuizRunnera) =====

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ===== SPRAWDZANIE =====

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// ===== POPRAWNA ODPOWIEDŹ =====

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// ===== ODPOWIEDŹ UŻYTKOWNIKA =====

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ===== WYJAŚNIENIE – W KROKACH =====

export function renderExplanation({ data }) {
  const { digit, conditionKind, correct } = data;

  const allDigits = [0,1,2,3,4,5,6,7,8,9];

  // 1) Dopuszczalne cyfry jedności
  let unitsDigits;
  let conditionSentence;

  if (conditionKind === "even") {
    unitsDigits = [0,2,4,6,8];
    conditionSentence =
      "liczba musi być parzysta, więc cyfra jedności musi należeć do zbioru";
  } else if (conditionKind === "odd") {
    unitsDigits = [1,3,5,7,9];
    conditionSentence =
      "liczba musi być nieparzysta, więc cyfra jedności musi należeć do zbioru";
  } else if (conditionKind === "div5") {
    unitsDigits = [0,5];
    conditionSentence =
      <>liczba ma być podzielna przez <InlineMath math="5" />, więc cyfra jedności musi należeć do zbioru</>;
  } else {
    unitsDigits = allDigits;
    conditionSentence =
      "brak dodatkowego warunku, więc cyfra jedności może być dowolna";
  }

  const unitsSetLatex = `\\{${unitsDigits.join(",")}\\}`;

  // 2) Przygotowanie liczb do rachunków
  // ile cyfr można postawić na poszczególnych miejscach gdy miejsce ≠ digit?
  const otherDigits = allDigits.filter(x => x !== digit);

  // REGUŁY:
  // setki: 1–9, ≠ digit
  const hundredsDigits = [1,2,3,4,5,6,7,8,9].filter(x => x !== digit);

  // dziesiątki: 0–9, ≠ digit
  const tensDigits = otherDigits;

  // jedności: musi być w unitsDigits i ≠ digit
  const unitsDigitsFiltered = unitsDigits.filter(x => x !== digit);

  // policzymy teraz każdy z 3 przypadków wprost:

  // ──────────────────────────────────────────────
  // PRZYPADEK 1 – cyfra digit w setkach
  // ──────────────────────────────────────────────
  const canDigitBeHundreds = digit !== 0;
  const case1_hundreds = canDigitBeHundreds ? 1 : 0; // setki nie mogą być równe 0
  const case1_tens = tensDigits.length;
  const case1_units = unitsDigitsFiltered.length;
  const case1_result = case1_hundreds * case1_tens * case1_units;

  // ──────────────────────────────────────────────
  // PRZYPADEK 2 – cyfra digit w dziesiątkach
  // ──────────────────────────────────────────────
  const case2_hundreds = hundredsDigits.length;
  const case2_tens = 1; // ustawione digit
  const case2_units = unitsDigitsFiltered.length;
  const case2_result = case2_hundreds * case2_tens * case2_units;

  // ──────────────────────────────────────────────
  // PRZYPADEK 3 – cyfra digit w jedności
  // ──────────────────────────────────────────────
  const case3_hundreds = hundredsDigits.length;
  const case3_tens = tensDigits.length;

  // warunek: cyfra jedności = digit, ale to możliwe tylko gdy digit ∈ unitsDigits
  const canDigitBeUnits = unitsDigits.includes(digit);
  const case3_units = canDigitBeUnits ? 1 : 0;

  const case3_result = case3_hundreds * case3_tens * case3_units;

  // suma
  const computedSum = case1_result + case2_result + case3_result;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      
      {/* KROK 1 */}
      <p>
        <strong>Krok 1. Warunek na cyfrę <InlineMath math={String(digit)} />.</strong>
      </p>
      <p>
        Szukamy liczb trzycyfrowych, w których cyfra{" "}
        <InlineMath math={`${digit}`} /> występuje <b>dokładnie raz</b>.
        Rozważamy więc trzy możliwości: stoi ona w setkach, dziesiątkach lub jedności.
      </p>

      {/* KROK 2 */}
      <p>
        <strong>Krok 2. Warunek na cyfrę jedności.</strong>
      </p>
      <p>
        Warunek na cyfrę jedności: {conditionSentence}. Dopuszczalne cyfry tworzą zbiór{" "}
        <InlineMath math={`U = ${unitsSetLatex}`} />.
      </p>

      {/* KROK 3 */}
      <p>
        <strong>Krok 3. Policz liczby w trzech przypadkach.</strong>
      </p>

      {/* PRZYPADEK 1 */}
      <p>
        <u>Przypadek <InlineMath math="1" />:</u> cyfra <InlineMath math={`${digit}`} /> stoi w setkach.<br />
        {canDigitBeHundreds ? (
          <>Wtedy:</>
        ) : (
          <>Niemożliwe, bo liczba trzycyfrowa nie może mieć <InlineMath math="0" /> na miejscu setek.</>
        )}
      </p>
      {canDigitBeHundreds ? (
        <>
          <ul>
            <li>dziesiątki: <InlineMath math={`${case1_tens}`} /> możliwości (dowolna cyfra różna od <InlineMath math={String(digit)} />)</li>
            <li>jedności: <InlineMath math={`${case1_units}`} /> możliwości (cyfra ze zbioru <InlineMath math={`U\\setminus\\{${digit}\\}`} />)</li>
          </ul>
          <p>
            Liczba możliwości:{" "}
            <InlineMath math={`${case1_hundreds} \\cdot ${case1_tens} \\cdot ${case1_units} = ${case1_result}`} />
          </p>
        </>
      ) : (
        <p>
          Liczba możliwości: <InlineMath math={`0`} />
        </p>
      )}

      {/* PRZYPADEK 2 */}
      <p>
        <u>Przypadek <InlineMath math="2" />:</u> cyfra <InlineMath math={`${digit}`} /> stoi w dziesiątkach.<br />
        Wtedy:
      </p>
      <ul>
        <li>setki: <InlineMath math={`${case2_hundreds}`} /> możliwości (cyfra ze zbioru <InlineMath math={`\\{1,\\ldots,9\\}\\setminus\\{${digit}\\}`} />)</li>
        <li>jedności: <InlineMath math={`${case2_units}`} /> możliwości</li>
      </ul>
      <p>
        Liczba możliwości:{" "}
        <InlineMath math={`${case2_hundreds} \\cdot 1 \\cdot ${case2_units} = ${case2_result}`} />
      </p>

      {/* PRZYPADEK 3 */}
      <p>
        <u>Przypadek <InlineMath math="3" />:</u> cyfra <InlineMath math={`${digit}`} /> stoi w jedności.<br />
        {canDigitBeUnits ? (
          <>
            Możliwe, ponieważ <InlineMath math={`${digit} \\in U`} />.
          </>
        ) : (
          <>
            Niemożliwe, ponieważ{" "}
            <InlineMath math={`${digit} \\notin U`} />, więc{" "}
            <InlineMath math={`${case3_result}`} /> przypadków.
          </>
        )}
      </p>
      {canDigitBeUnits && (
        <>
          <ul>
            <li>setki: <InlineMath math={`${case3_hundreds}`} /> możliwości</li>
            <li>dziesiątki: <InlineMath math={`${case3_tens}`} /> możliwości</li>
          </ul>
          <p>
            Liczba możliwości:{" "}
            <InlineMath math={`${case3_hundreds} \\cdot ${case3_tens} \\cdot 1 = ${case3_result}`} />
          </p>
        </>
      )}

      {/* PODSUMOWANIE */}
      <p>
        <strong>Krok 4. Suma przypadków.</strong>
      </p>
      <p>
        <InlineMath
          math={`${case1_result} + ${case2_result} + ${case3_result} = ${computedSum}`}
        />
      </p>

      <p>
        Ostatecznie otrzymujemy{" "}
        <strong>
          <InlineMath math={`${correct}`} />
        </strong>{" "}
        liczb spełniających warunki zadania.
      </p>
    </div>
  );
}
